import { z } from "zod";
import { localeSchema } from "./locale.js";
import { personNamePartSchema } from "./person-name.js";
// Profilo dell'utente e sue appartenenze (piano M1a Task 12). Rotte dell'identità, fuori da ogni azienda:
// nessuna chiede l'header di appartenenza. L'utente modifica soltanto sé stesso e il cambio dell'email non
// è in M1a. Il nome qui è quello dell'identità; il nome con cui la persona compare in un'azienda è
// dell'appartenenza e lo cambia chi gestisce il team (interpretazione provvisoria del piano, Global
// Constraints).
export const meSchema = z.object({
    id: z.string(),
    email: z.string(),
    /** Nome e cognome dell'identità; `null` per un'identità nata prima che fossero due campi, finché non li scrive. */
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    /** Nome intero derivato da nome e cognome (per un'identità vecchia, quello di prima). */
    name: z.string(),
    /**
     * `null`: nessuna scelta personale, vale la lingua del dispositivo (decisione del product owner sulla questione
     * 39, 30 settembre 2026). Una volta scelta, la lingua resta `"it"` o `"en"`: non si torna a `null`.
     */
    preferredLocale: localeSchema.nullable(),
    isPlatformAdmin: z.boolean(),
});
export const meResponseSchema = z.object({ user: meSchema });
/**
 * Corpo di `PATCH /api/me`: almeno un campo; nessun campo indica un'altra persona. Nome e cognome si possono mandare
 * anche uno solo, se l'altro è già salvato; il nome intero si ricalcola dai due.
 */
export const updateMeBodySchema = z
    .strictObject({
    firstName: personNamePartSchema.optional(),
    lastName: personNamePartSchema.optional(),
    /**
     * Soltanto `"it"` o `"en"`: la lingua è una scelta personale e non c'è un ritorno alla lingua dell'azienda
     * (decisione del product owner sulla questione 39, 30 settembre 2026); `null` è un campo non valido.
     */
    preferredLocale: localeSchema.optional(),
})
    .refine((body) => Object.values(body).some((value) => value !== undefined), { message: "At least one field is required" });
/** Un'appartenenza attiva di un'azienda attiva: la SPA la usa in M1b per scegliere l'appartenenza della scheda. */
export const myMembershipSchema = z.object({
    membershipId: z.string(),
    organizationId: z.string(),
    organizationName: z.string(),
    /** L'indirizzo breve dell'azienda: `/<organizationSlug>` apre direttamente quell'azienda (decisione del 3 ottobre 2026). */
    organizationSlug: z.string(),
    /** Nome e cognome con cui la persona compare in quell'azienda; `displayName` è il nome intero derivato. */
    firstName: z.string(),
    lastName: z.string(),
    displayName: z.string(),
    roleName: z.string(),
});
export const myMembershipsResponseSchema = z.object({ memberships: z.array(myMembershipSchema) });
const contextRoleSchema = z.object({ id: z.string(), name: z.string(), level: z.number().int().min(1).max(4), isSystem: z.boolean() });
/** Area → azione → ambito; un'area o un'azione assente vale `none`. */
const contextAreaPermissionsSchema = z.record(z.string(), z.record(z.string(), z.enum(["none", "own", "all"])));
/**
 * La persona osservata con «Guarda come…» (la Vista Commerciale) nella scheda (decisioni del product owner del 2 ottobre
 * 2026): una persona attiva dell'azienda di qualunque ruolo, con il suo ruolo, le sue funzioni effettive e i suoi
 * permessi, in sola lettura. L'interfaccia li usa per mostrare il menu che vedrebbe lei e per stringere i comandi dei Task
 * (`session/access.ts`: «Richiamo», «Sposta» e le altre azioni si offrono soltanto se lo permettono sia chi guarda sia la
 * persona osservata, come fa il server in `tasks/access.ts`); gli altri poteri della scheda restano quelli di chi guarda
 * (`features` e `areaPermissions` del contesto), perché la vista non è impersonazione.
 */
export const observedCommercialSchema = z.object({
    membershipId: z.string(),
    displayName: z.string(),
    role: contextRoleSchema,
    features: z.array(z.string()),
    areaPermissions: contextAreaPermissionsSchema,
});
/**
 * Una persona che chi guarda può scegliere in «Guarda come…» (`GET /api/commercial-view/people`): attiva, della stessa
 * azienda, mai chi guarda, e con un ruolo che non ha più poteri del suo (il Super admin di Gioia365 le vede tutte, il
 * Super admin dell'azienda tutte tranne sé stesso). `isCustom` dice un ruolo su misura (una copia), che il selettore
 * mette dopo i ruoli di partenza.
 */
export const observablePersonSchema = z.object({
    membershipId: z.string(),
    displayName: z.string(),
    role: contextRoleSchema.extend({ isCustom: z.boolean() }),
});
/**
 * Le persone del selettore, nell'ordine dei gruppi: Super admin, Admin, Super user e User (i ruoli di partenza, dal
 * livello più alto), poi i ruoli su misura (livello, poi nome); dentro un ruolo per nome.
 */
export const observablePeopleResponseSchema = z.object({ people: z.array(observablePersonSchema) });
/**
 * Contesto della scheda (piano M1b D4): ciò che il server concede all'appartenenza dichiarata dalla scheda, o
 * al gestore della piattaforma nel contesto esplicito di un'azienda. L'interfaccia lo usa soltanto per
 * decidere cosa mostrare; ogni rotta controlla comunque il permesso da sé. A differenza delle rotte `/api/me`
 * qui serve l'header di appartenenza, perché il ruolo e l'azienda sono della scheda.
 */
export const myContextSchema = z.object({
    /** Only the protected owner can manage billing; absent on older clients means no billing menu. */
    isProtectedOwner: z.boolean().optional(),
    /** Owner access is restricted to Team recovery and billing while CRM is blocked. */
    billingRecovery: z.boolean().optional(),
    kind: z.enum(["membership", "platform"]),
    membershipId: z.string().nullable(),
    // `slug`: l'indirizzo breve, che il programma mette davanti agli indirizzi delle pagine dell'azienda (4 ottobre 2026).
    organization: z.object({ id: z.string(), name: z.string(), slug: z.string() }),
    /** Nome e cognome con cui la persona compare nell'azienda e il nome intero derivato; null nel contesto di piattaforma. */
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    displayName: z.string().nullable(),
    role: contextRoleSchema.nullable(),
    /** Funzioni e sezioni delle Impostazioni che il server concede in questa scheda. */
    features: z.array(z.string()),
    areaPermissions: contextAreaPermissionsSchema,
    settings: z.object({ timeZone: z.string(), defaultLocale: localeSchema }),
    /** Il Commerciale della Vista Commerciale della richiesta (header `X-Gioia-Commercial-View`), oppure `null`. */
    commercialView: observedCommercialSchema.nullable(),
});
export const myContextResponseSchema = z.object({ context: myContextSchema });
//# sourceMappingURL=me.js.map